import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import {
  CheckCircle2, Circle, ListTodo, Plus, MoreHorizontal, Sparkles,
  MessageSquare, Archive, Trash2, Download, Flag, CalendarClock, Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { smartTasksService, tasksQueryKey, suggestionsQueryKey, archivedTasksQueryKey, type TaskSuggestion } from "@/services/smartTasksService";
import { sortTasks, TASK_PRIORITY_COLORS, type SmartTask, type TaskPriority } from "@/lib/smartTasksPure";

type Tab = "mine" | "assigned" | "suggested" | "done" | "archived";

/**
 * Smart Tasks workspace (DS EDIT 2 jul26). Iceberg: quick-add is the one
 * primary action; tabs are progressive disclosure; everything else lives in
 * per-task menus. Calm language throughout — no urgency pressure.
 */
export default function SmartTasks() {
  const { user } = useAuth();
  const { currentWorkspaceId } = useWorkspace();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();

  const [tab, setTab] = useState<Tab>("mine");
  const [quickTitle, setQuickTitle] = useState("");
  const [quickDue, setQuickDue] = useState("");

  const tasksQuery = useQuery({
    queryKey: tasksQueryKey(user?.id),
    queryFn: () => smartTasksService.listTasks(),
    enabled: !!user,
  });
  const suggestionsQuery = useQuery({
    queryKey: suggestionsQueryKey(user?.id),
    queryFn: () => smartTasksService.listPendingSuggestions(),
    enabled: !!user,
  });
  const archivedQuery = useQuery({
    queryKey: archivedTasksQueryKey(user?.id),
    queryFn: () => smartTasksService.listArchived(),
    enabled: !!user,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: tasksQueryKey(user?.id) });
    void queryClient.invalidateQueries({ queryKey: suggestionsQueryKey(user?.id) });
    void queryClient.invalidateQueries({ queryKey: archivedTasksQueryKey(user?.id) });
  };

  const tasks = tasksQuery.data ?? [];
  const suggestions = suggestionsQuery.data ?? [];
  const archived = archivedQuery.data ?? [];
  const active = useMemo(() => sortTasks(tasks.filter((t) => t.status === "open" || t.status === "in_progress"), Date.now()), [tasks]);
  const done = useMemo(() => tasks.filter((t) => t.status === "done"), [tasks]);
  const mine = active.filter((t) => t.ownerUserId === user?.id);
  const assignedToMe = active.filter((t) => t.assigneeUserId === user?.id && t.ownerUserId !== user?.id);
  const doneThisWeek = done.filter((t) => t.completedAt && Date.now() - Date.parse(t.completedAt) < 7 * 864e5).length;

  const createMutation = useMutation({
    mutationFn: () => smartTasksService.createTask({
      title: quickTitle,
      dueAt: quickDue ? new Date(quickDue).toISOString() : null,
      workspaceId: currentWorkspaceId ?? null,
    }),
    onSuccess: () => { setQuickTitle(""); setQuickDue(""); invalidate(); },
    onError: (e: Error) => toast({ title: "We couldn't save that on our end. Try again?", description: e.message, variant: "destructive" }),
  });

  const patchMutation = useMutation({
    mutationFn: (input: { id: string; patch: Parameters<typeof smartTasksService.updateTask>[1] }) =>
      smartTasksService.updateTask(input.id, input.patch),
    onSuccess: invalidate,
    onError: (e: Error) => toast({ title: "We couldn't save that on our end. Try again?", description: e.message, variant: "destructive" }),
  });

  const approveMutation = useMutation({
    mutationFn: (s: TaskSuggestion) => smartTasksService.approveSuggestion(s),
    onSuccess: () => { toast({ title: "Task created" }); invalidate(); },
    onError: (e: Error) => toast({ title: "Couldn't approve", description: e.message, variant: "destructive" }),
  });
  const dismissMutation = useMutation({
    mutationFn: (id: string) => smartTasksService.dismissSuggestion(id),
    onSuccess: invalidate,
  });

  const discuss = async (t: SmartTask) => {
    try {
      const threadId = await smartTasksService.openDiscussion(t);
      navigate(`/chat?threadId=${threadId}`);
    } catch (e) {
      toast({ title: "Couldn't open discussion", description: (e as Error).message, variant: "destructive" });
    }
  };

  const exportData = async () => {
    const data = await smartTasksService.exportMyTasks();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "deeply-smart-tasks-export.json";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const dueBadge = (t: SmartTask) => {
    if (!t.dueAt) return null;
    const due = Date.parse(t.dueAt);
    const overdue = due < Date.now();
    return (
      <Badge variant={overdue ? "destructive" : "secondary"} className="text-[10px] gap-1">
        <CalendarClock className="h-3 w-3" />
        {new Date(due).toLocaleDateString()}
      </Badge>
    );
  };

  const priorityFlag = (p: TaskPriority) =>
    p !== "none" ? (
      <Flag className="h-3.5 w-3.5 shrink-0" aria-label={`Priority: ${p}`}
        style={{ color: TASK_PRIORITY_COLORS[p as keyof typeof TASK_PRIORITY_COLORS] }} />
    ) : null;

  const TaskRow = ({ t }: { t: SmartTask }) => (
    <div className="flex items-center gap-2 py-2 border-b border-border/50 last:border-0" data-testid={`task-row-${t.id}`}>
      <button
        aria-label={t.status === "done" ? "Reopen task" : "Complete task"}
        className="shrink-0 p-1 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center"
        onClick={() => patchMutation.mutate({ id: t.id, patch: { status: t.status === "done" ? "open" : "done" } })}
      >
        {t.status === "done"
          ? <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          : <Circle className="h-5 w-5 text-muted-foreground" />}
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          {priorityFlag(t.priority)}
          <span className={`truncate text-sm ${t.status === "done" ? "line-through text-muted-foreground" : ""}`}>{t.title}</span>
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          {dueBadge(t)}
          {t.workspaceId && <Badge variant="outline" className="text-[10px]">workspace</Badge>}
          {t.assigneeUserId && t.assigneeUserId !== user?.id && <Badge variant="outline" className="text-[10px]">assigned</Badge>}
          {t.origin !== "manual" && <Badge variant="outline" className="text-[10px] gap-0.5"><Sparkles className="h-2.5 w-2.5" />AI</Badge>}
        </div>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Task options">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem onClick={() => void discuss(t)}>
            <MessageSquare className="h-4 w-4 mr-2" />{t.discussionThreadId ? "Open discussion" : "Discuss in chat"}
          </DropdownMenuItem>
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wide">Priority</DropdownMenuLabel>
          <div className="flex gap-1 px-2 pb-1.5" role="group" aria-label="Set priority">
            {(["none", "low", "medium", "high", "urgent"] as const).map((lvl) => (
              <button key={lvl} type="button" title={lvl}
                aria-pressed={t.priority === lvl}
                onClick={() => patchMutation.mutate({ id: t.id, patch: { priority: lvl } })}
                className={`h-5 flex-1 rounded border text-[10px] ${t.priority === lvl ? "border-primary ring-1 ring-primary" : "border-border"}`}
                style={lvl !== "none" ? { background: TASK_PRIORITY_COLORS[lvl as keyof typeof TASK_PRIORITY_COLORS], opacity: t.priority === lvl ? 1 : 0.5 } : undefined}
              >{lvl === "none" ? "—" : ""}</button>
            ))}
          </div>
          <DropdownMenuSeparator />
          {tab === "archived" ? (
            <DropdownMenuItem onClick={() => patchMutation.mutate({ id: t.id, patch: { status: "open" } })}>
              <Archive className="h-4 w-4 mr-2" />Restore
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onClick={() => patchMutation.mutate({ id: t.id, patch: { status: "archived" } })}>
              <Archive className="h-4 w-4 mr-2" />Archive
            </DropdownMenuItem>
          )}
          {t.ownerUserId === user?.id && (
            <DropdownMenuItem className="text-destructive" onClick={() => smartTasksService.deleteTask(t.id).then(invalidate)}>
              <Trash2 className="h-4 w-4 mr-2" />Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  const list: SmartTask[] = tab === "mine" ? mine : tab === "assigned" ? assignedToMe : tab === "done" ? done : tab === "archived" ? archived : [];

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><ListTodo className="h-6 w-6" /> Smart Tasks</h1>
          {doneThisWeek > 0 && (
            <p className="text-sm text-muted-foreground mt-1">You completed {doneThisWeek} task{doneThisWeek === 1 ? "" : "s"} this week.</p>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="More options"><MoreHorizontal className="h-4 w-4" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => void exportData()}><Download className="h-4 w-4 mr-2" />Export my task data</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Quick add — the one primary action */}
      <Card>
        <CardContent className="pt-4 flex gap-2 flex-wrap">
          <Input
            value={quickTitle}
            onChange={(e) => setQuickTitle(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && quickTitle.trim() && createMutation.mutate()}
            placeholder="Add a task…"
            className="flex-1 min-w-[200px]"
            data-testid="input-quick-task"
          />
          <Input type="date" value={quickDue} onChange={(e) => setQuickDue(e.target.value)} className="w-auto" aria-label="Due date (optional)" />
          <Button onClick={() => createMutation.mutate()} disabled={createMutation.isPending || !quickTitle.trim()} data-testid="button-add-task">
            {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            <span className="ml-1">Add</span>
          </Button>
        </CardContent>
      </Card>

      {/* Tabs — progressive disclosure */}
      <div className="flex gap-1.5 flex-wrap" role="tablist" aria-label="Task views">
        {([
          ["mine", `My tasks${mine.length ? ` (${mine.length})` : ""}`],
          ["assigned", `Assigned to me${assignedToMe.length ? ` (${assignedToMe.length})` : ""}`],
          ["suggested", `Suggested${suggestions.length ? ` (${suggestions.length})` : ""}`],
          ["done", "Completed"],
          ["archived", `Archived${archived.length ? ` (${archived.length})` : ""}`],
        ] as Array<[Tab, string]>).map(([key, label]) => (
          <Button key={key} role="tab" aria-selected={tab === key} size="sm"
            variant={tab === key ? "default" : "outline"} onClick={() => setTab(key)}>
            {label}
          </Button>
        ))}
      </div>

      <Card>
        <CardContent className="pt-4">
          {tasksQuery.isLoading ? (
            <div className="space-y-2"><Skeleton className="h-8 w-full" /><Skeleton className="h-8 w-4/5" /><Skeleton className="h-8 w-5/6" /></div>
          ) : tab === "suggested" ? (
            suggestions.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">
                No suggestions waiting. Open a thread and choose "Suggest tasks" — would you like Deeply Smart to suggest follow-ups?
              </p>
            ) : (
              <div className="space-y-3">
                {suggestions.map((s) => (
                  <div key={s.id} className="border rounded-md p-3" data-testid={`suggestion-${s.id}`}>
                    <div className="flex items-center gap-1.5">
                      <Sparkles className="h-3.5 w-3.5 text-primary shrink-0" />
                      <span className="text-sm font-medium">{s.proposed.title}</span>
                    </div>
                    {s.reason && <p className="text-xs text-muted-foreground mt-1">Why: {s.reason}</p>}
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" onClick={() => approveMutation.mutate(s)} disabled={approveMutation.isPending}>Ready to pick this up?</Button>
                      <Button size="sm" variant="ghost" onClick={() => dismissMutation.mutate(s.id)}>Dismiss</Button>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : list.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">
              {tab === "done" ? "Completed tasks will rest here." : tab === "archived" ? "No archived tasks yet." : tab === "assigned" ? "Nothing assigned to you right now." : "All clear. Add a task above when you're ready."}
            </p>
          ) : (
            list.map((t) => <TaskRow key={t.id} t={t} />)
          )}
        </CardContent>
      </Card>
    </div>
  );
}
